import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';

import { useContent } from '@/hooks/useContent';
import { useConfig, isYes } from '@/hooks/useConfig';
import { authUserMetaAtom } from '@/store/authAtoms';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { examService, type DicomMediaListItem, type ExamRecord } from '@/services/examService';
import { messageService, messageKeys } from '@/services/messageService';
import { hasOpenableReport } from '@/lib/examRecords';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useSessionCaps } from '@/hooks/useSessionCaps';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';

const SEEN_MEDIA_KEY = 'pp_seen_dicom_media';
const DISMISS_EXAM_SOON_KEY = 'pp_notif_exam_soon_dismissed';

type NotifKind = 'examSoon' | 'downloadReady' | 'newResults' | 'readyToSchedule' | 'message';

interface PortalNotification {
  id: string;
  kind: NotifKind;
  title: string;
  whenLabel: string;
  unread: boolean;
  href: string;
  state?: Record<string, unknown>;
}

function getSeenMediaSet(): Set<number> {
  try { return new Set<number>(JSON.parse(localStorage.getItem(SEEN_MEDIA_KEY) ?? '[]') as number[]); }
  catch { return new Set(); }
}

function relativeWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  const mins = dayjs().diff(d, 'minute');
  if (mins < 60) return `${Math.max(mins, 1)} mins ago`;
  const hours = dayjs().diff(d, 'hour');
  if (hours < 24) return `${hours} hr ago`;
  return d.format('dddd');
}

function kindIcon(kind: NotifKind): ReactNode {
  const sx = { fontSize: 20, color: 'primary.main' } as const;
  switch (kind) {
    case 'examSoon':
    case 'readyToSchedule':
      return <CalendarMonthOutlinedIcon sx={sx} />;
    case 'newResults':
      return <AssignmentOutlinedIcon sx={sx} />;
    case 'downloadReady':
      return <DownloadOutlinedIcon sx={sx} />;
    case 'message':
      return <ChatBubbleOutlineIcon sx={sx} />;
  }
}

function parseExamSoonWindow(raw: string | undefined): number {
  const n = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n >= 0 ? n : 3;
}

/**
 * Header bell + All/Unread panel (popover on desktop, drawer on mobile).
 * Surfaces exam-soon, download-ready, new results, ready-to-schedule, and unread messages
 * from existing dashboard/query caches — not a separate notifications product.
 */
export function PortalNotifications() {
  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const navigate = useNavigate();
  const { getContentString } = useContent();
  const lite = useLiteMode();
  const { isGuestSession, isICodeSession } = useSessionCaps();
  const user = useAtomValue(authUserMetaAtom);
  const isRemoteAccess = useAtomValue(remoteAccessContextAtom) !== null;
  const messagingEnabled = isYes(useConfig('PPMessagesAccessEnabled'));
  const pacsImageDownloading = isYes(useConfig('PACSImageDownloading'));
  const examSoonWindow = parseExamSoonWindow(useConfig('DaysBeforeExamToShowNotification'));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const open = isDesktop ? Boolean(anchorEl) : mobileOpen;

  const canFetchWorklist = !!user?.id && !isGuestSession && !isICodeSession;
  const canFetchMessages = canFetchWorklist && !isRemoteAccess && messagingEnabled && lite.isSectionVisible('messages');

  const { data: examRecords = [] } = useQuery({
    queryKey: ['exams', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'exams' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('exams'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: apptRecords = [] } = useQuery({
    queryKey: ['appointments', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'appointments' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('appointments'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: inboxPage } = useQuery({
    queryKey: messageKeys.inbox(1, user?.id),
    queryFn: () => messageService.getInbox(1),
    enabled: canFetchMessages,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const { data: activeDicomMedia = [] } = useQuery({
    queryKey: ['dicomMediaActive', user?.id],
    queryFn: () => examService.listActiveDicomMedia(),
    enabled: canFetchWorklist && !isRemoteAccess && pacsImageDownloading,
    staleTime: 60_000,
    retry: false,
  });

  const notifications = useMemo(() => {
    const items: PortalNotification[] = [];
    const now = dayjs();
    const dismissedExamSoon = sessionStorage.getItem(DISMISS_EXAM_SOON_KEY) === '1';
    const seenMedia = getSeenMediaSet();

    const upcoming = (apptRecords as ExamRecord[])
      .filter((r) => r.reportStatus === 'Scheduled' && dayjs(r.studyDate).isAfter(now))
      .sort((a, b) => dayjs(a.studyDate).diff(dayjs(b.studyDate)));
    const next = upcoming[0];
    if (next && !dismissedExamSoon) {
      const days = dayjs(next.studyDate).startOf('day').diff(now.startOf('day'), 'day');
      if (days >= 0 && days <= examSoonWindow) {
        items.push({
          id: `exam-soon-${next.studyKey}`,
          kind: 'examSoon',
          title: getContentString('PPNotificationsExamSoon') || 'Upcoming appointment',
          whenLabel: days === 0
            ? (getContentString('PPDashboardNextApptToday') || 'Today')
            : days === 1
              ? (getContentString('PPDashboardNextApptTomorrow') || 'Tomorrow')
              : (getContentString('PPDashboardNextApptInDays') || 'In {{count}} days').replace('{{count}}', String(days)),
          unread: true,
          href: '/appointments',
          state: { selectedApptId: String(next.studyKey) },
        });
      }
    }

    const readyDownloads = (activeDicomMedia as DicomMediaListItem[]).filter(
      (i) => (i.isComplete || i.isAvailable) && !seenMedia.has(i.mediaRequestKey),
    );
    for (const media of readyDownloads.slice(0, 3)) {
      items.push({
        id: `download-${media.mediaRequestKey}`,
        kind: 'downloadReady',
        title: getContentString('PPNotificationsDownloadReady') || 'Images ready to download',
        whenLabel: media.examDescription || media.accessionNumber || '',
        unread: true,
        href: media.studyKey > 0 ? '/exams' : '/account',
        state: media.studyKey > 0
          ? { selectedExamId: String(media.studyKey) }
          : { accountSection: 'imageDownload' },
      });
    }

    const newResults = (examRecords as ExamRecord[]).filter((r) => hasOpenableReport(r) && r.isNewReport === true);
    if (newResults.length > 0) {
      const newest = [...newResults].sort((a, b) => dayjs(b.studyDate).valueOf() - dayjs(a.studyDate).valueOf())[0];
      items.push({
        id: `new-results-${newest.studyKey}`,
        kind: 'newResults',
        title: getContentString('PPNotificationsNewResults') || 'Exam Results',
        whenLabel: relativeWhen(newest.studyDate),
        unread: true,
        href: '/exams',
        state: { selectedExamId: String(newest.studyKey) },
      });
    }

    const pending = (apptRecords as ExamRecord[]).filter((r) => r.reportStatus !== 'Scheduled');
    if (pending.length > 0) {
      items.push({
        id: `ots-${pending[0].studyKey}`,
        kind: 'readyToSchedule',
        title: getContentString('PPNotificationsReadyToSchedule') || 'Appointment ready to schedule',
        whenLabel: pending[0].procedureDescription || '',
        unread: true,
        href: '/appointments',
      });
    }

    if (inboxPage?.messagingEnabled !== false) {
      for (const msg of (inboxPage?.items ?? []).filter((m) => !m.readFlag).slice(0, 5)) {
        items.push({
          id: `msg-${msg.messageId}`,
          kind: 'message',
          title: getContentString('PPNotificationsNewMessage') || 'Message: Provider has sent you a message',
          whenLabel: relativeWhen(msg.sentDateTime),
          unread: true,
          href: '/messages',
        });
      }
    }

    return items;
  }, [apptRecords, examRecords, activeDicomMedia, inboxPage, examSoonWindow, getContentString]);

  const unreadCount = notifications.filter((n) => n.unread).length;
  const visible = filter === 'unread' ? notifications.filter((n) => n.unread) : notifications;

  const handleOpen = (el: HTMLElement) => {
    if (isDesktop) setAnchorEl(el);
    else setMobileOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const handleSelect = (item: PortalNotification) => {
    if (item.kind === 'examSoon') sessionStorage.setItem(DISMISS_EXAM_SOON_KEY, '1');
    handleClose();
    navigate(item.href, item.state ? { state: item.state } : undefined);
  };

  const panel = (
    <Box sx={{ width: { xs: '100%', sm: 360 }, maxHeight: { xs: '85vh', sm: 480 }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {getContentString('PPNotificationsTitle') || 'Notifications'}
        </Typography>
        {!isDesktop && (
          <IconButton size="small" onClick={handleClose} aria-label={getContentString('PortalClose') || 'Close'}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Tabs
        value={filter}
        onChange={(_, v: 'all' | 'unread') => setFilter(v)}
        sx={{ px: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab value="all" label={getContentString('PPNotificationsAll') || 'All'} />
        <Tab
          value="unread"
          label={(
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <span>{getContentString('PPNotificationsUnread') || 'Unread'}</span>
              {unreadCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: 9,
                    bgcolor: 'info.main',
                    color: 'info.contrastText',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unreadCount}
                </Box>
              )}
            </Stack>
          )}
        />
      </Tabs>
      <Stack spacing={1} sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
        {visible.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
            {getContentString('PPNotificationsEmpty') || 'No notifications'}
          </Typography>
        )}
        {visible.map((item) => (
          <Button
            key={item.id}
            onClick={() => handleSelect(item)}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'start',
              textTransform: 'none',
              color: 'text.primary',
              bgcolor: alpha(theme.palette.action.hover, 0.6),
              borderRadius: 2,
              px: 1.25,
              py: 1.25,
              gap: 1.25,
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{kindIcon(item.kind)}</Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{item.title}</Typography>
              {item.whenLabel && (
                <Typography variant="caption" color="text.secondary">{item.whenLabel}</Typography>
              )}
            </Box>
            {item.unread && <FiberManualRecordIcon sx={{ fontSize: 10, color: 'info.main', flexShrink: 0 }} />}
          </Button>
        ))}
      </Stack>
    </Box>
  );

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={`${getContentString('PPNotificationsTitle') || 'Notifications'}${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => handleOpen(e.currentTarget)}
        sx={{ minWidth: 44, minHeight: 44, color: 'text.secondary' }}
      >
        <Badge
          badgeContent={unreadCount}
          color="info"
          max={99}
          sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}
        >
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      {isDesktop ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, overflow: 'hidden' } } }}
        >
          {panel}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '90vh' } }}
        >
          {panel}
        </Drawer>
      )}
    </>
  );
}

/** Shared badge counts for bottom nav / sidebar (reuses Dashboard worklist query keys). */
export function usePortalNavBadges() {
  const user = useAtomValue(authUserMetaAtom);
  const isRemoteAccess = useAtomValue(remoteAccessContextAtom) !== null;
  const { isGuestSession, isICodeSession } = useSessionCaps();
  const lite = useLiteMode();
  const messagingEnabled = isYes(useConfig('PPMessagesAccessEnabled'));
  const canFetchWorklist = !!user?.id && !isGuestSession && !isICodeSession;

  const { data: examRecords = [] } = useQuery({
    queryKey: ['exams', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'exams' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('exams'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: apptRecords = [] } = useQuery({
    queryKey: ['appointments', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'appointments' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('appointments'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: inboxPage } = useQuery({
    queryKey: messageKeys.inbox(1, user?.id),
    queryFn: () => messageService.getInbox(1),
    enabled: canFetchWorklist && !isRemoteAccess && messagingEnabled && lite.isSectionVisible('messages'),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const newResults = useMemo(
    () => examRecords.filter((r) => hasOpenableReport(r) && r.isNewReport === true).length,
    [examRecords],
  );
  const upcoming = useMemo(() => {
    const now = dayjs();
    return apptRecords.filter((r) => r.reportStatus === 'Scheduled' && dayjs(r.studyDate).isAfter(now)).length;
  }, [apptRecords]);
  const unreadMessages =
    inboxPage?.messagingEnabled === false
      ? 0
      : (inboxPage?.items ?? []).filter((item) => !item.readFlag).length;

  return { newResults, upcoming, unreadMessages };
}
